import { useEffect, useState } from "react";
import { ViewerId } from "../../atom";

/**
 * 듀얼 뷰어에서 서로의 OpenSeadragon 인스턴스를 참조하기 위한 등록소.
 * 뷰어 인스턴스는 Recoil 에 넣기 부적합(변경 가능한 큰 객체)이라 모듈 단위로 둔다.
 */
type ViewerMap = Partial<Record<ViewerId, any>>;

let viewers: ViewerMap = {};
const listeners = new Set<(next: ViewerMap) => void>();

const emit = () => listeners.forEach((listener) => listener(viewers));

export const registerViewer = (viewerId: ViewerId, viewer: any) => {
  viewers = { ...viewers, [viewerId]: viewer };
  emit();
};

/** 같은 id 로 이미 다른 인스턴스가 등록돼 있으면(새 뷰어가 먼저 마운트) 지우지 않는다 */
export const unregisterViewer = (viewerId: ViewerId, viewer: any) => {
  if (viewers[viewerId] !== viewer) return;
  const { [viewerId]: _removed, ...rest } = viewers;
  viewers = rest;
  emit();
};

export const useRegisteredViewers = (): ViewerMap => {
  const [current, setCurrent] = useState<ViewerMap>(viewers);
  useEffect(() => {
    listeners.add(setCurrent);
    setCurrent(viewers);
    return () => {
      listeners.delete(setCurrent);
    };
  }, []);
  return current;
};

/**
 * 이미지 1px 이 화면 몇 px 로 보이는지(image zoom) 와 뷰포트 zoom 사이 변환.
 * 슬라이드 배율 표시(scalebar)와 같은 식: 현재 배율 = 스캔 배율(slideMagnification) × image zoom
 */
const getItem = (viewer: any) => viewer?.world?.getItemAt?.(0);

const toImageZoom = (viewer: any, viewportZoom: number) =>
  getItem(viewer)?.viewportToImageZoom?.(viewportZoom);

const toViewportZoom = (viewer: any, imageZoom: number) =>
  getItem(viewer)?.imageToViewportZoom?.(imageZoom);

/**
 * 두 뷰어를 같은 배율로 맞춰 함께 움직인다. 해제 함수를 돌려준다.
 * - 양쪽 다 슬라이드(스캔 배율 있음)면 화면에 표시되는 배율(예: 10 / 40X 의 10)을 같게 맞춘다.
 * - 한쪽이라도 일반 이미지면 배율 정보가 없으므로 같은 픽셀 비율(이미지 1px = 화면 같은 px)로 맞춘다.
 * - 확대 기준점(마우스 위치)도 반대편의 같은 화면 상대 위치로 따라간다. 기준점이 없으면(버튼 등) 중앙 기준.
 * - 사용자가 조작 중인(마우스가 올라간) 뷰어의 줌만 전파한다. 이미지를 새로 열 때의
 *   초기 줌(goHome) 같은 자동 변화가 다른 뷰어로 번지지 않게 하기 위함.
 * - 연결하는 순간 source 쪽 배율로 반대편을 바로 맞춘다.
 */
export const linkViewerZoom = (a: any, b: any, source: any = a) => {
  let syncing = false;

  const bothHaveMagnification = () => a.slideMagnification > 0 && b.slideMagnification > 0;
  const scanMag = (viewer: any) => (bothHaveMagnification() ? viewer.slideMagnification : 1);

  // 확대 기준점(마우스 위치)을 반대편의 같은 화면 상대 위치로 옮긴다.
  // 예: 왼쪽 뷰어의 가로 30%·세로 40% 지점에서 휠 → 오른쪽 뷰어도 30%·40% 지점 기준으로 확대.
  const mapRefPoint = (from: any, to: any, refPoint: any) => {
    if (!refPoint) return undefined;
    const fromW = from.container?.clientWidth;
    const fromH = from.container?.clientHeight;
    const toW = to.container?.clientWidth;
    const toH = to.container?.clientHeight;
    if (!fromW || !fromH || !toW || !toH) return undefined;
    const pixel = from.viewport.pixelFromPoint(refPoint, true);
    // OpenSeadragon.Point 는 prototype 을 통째로 교체해 constructor 로 새로 만들 수 없다.
    // pixel 을 복제(times(1))해 좌표만 바꿔 Point 메서드(minus 등)를 유지한다.
    const toPixel = pixel.times(1);
    toPixel.x = (pixel.x / fromW) * toW;
    toPixel.y = (pixel.y / fromH) * toH;
    return to.viewport.pointFromPixel(toPixel, true);
  };

  const matchZoom = (
    from: any,
    to: any,
    fromViewportZoom: number,
    immediately = false,
    fromRefPoint: any = null
  ) => {
    if (!to.viewport || !from.viewport) return;
    const imageZoom = toImageZoom(from, fromViewportZoom);
    if (!imageZoom) return;
    const magnification = imageZoom * scanMag(from);
    const targetZoom = toViewportZoom(to, magnification / scanMag(to));
    if (!targetZoom || !Number.isFinite(targetZoom)) return;

    syncing = true;
    try {
      to.viewport.zoomTo(targetZoom, mapRefPoint(from, to, fromRefPoint), immediately);
      to.viewport.applyConstraints();
    } finally {
      syncing = false;
    }
  };

  const makeHandler = (from: any, to: any) => (event: any) => {
    if (syncing || !event.zoom) return;
    if (!from.container?.matches?.(":hover")) return;
    matchZoom(from, to, event.zoom, false, event.refPoint);
  };

  const handleA = makeHandler(a, b);
  const handleB = makeHandler(b, a);
  a.addHandler("zoom", handleA);
  b.addHandler("zoom", handleB);

  const target = source === b ? a : b;
  matchZoom(source, target, source.viewport?.getZoom?.(), true);

  return () => {
    a.removeHandler?.("zoom", handleA);
    b.removeHandler?.("zoom", handleB);
  };
};

/**
 * 듀얼 뷰어 양쪽 화면을 왼쪽·오른쪽 나란히 한 장으로 캡처해 내려받는다.
 * 각 뷰어는 화면에 보이는 그대로(captureToCanvas) 틈 없이 붙여 그리고, 높이가 다르면 세로 가운데 정렬.
 */
export const downloadDualCapture = async ({
  withAnnotations = false,
  filename = "capture-dual.png",
}: { withAnnotations?: boolean; filename?: string } = {}) => {
  const left = viewers.left;
  const right = viewers.right;
  if (!left?.captureToCanvas || !right?.captureToCanvas) return null;

  try {
    // captureToCanvas 가 어노테이션을 그렸는지 annotationsDrawn 으로 알려준다
    const [leftCanvas, rightCanvas]: (HTMLCanvasElement & { annotationsDrawn?: boolean })[] =
      await Promise.all([
      left.captureToCanvas({ withAnnotations }),
      right.captureToCanvas({ withAnnotations }),
    ]);

    const annotationsDrawn = !!leftCanvas.annotationsDrawn && !!rightCanvas.annotationsDrawn;
    const canvas = document.createElement("canvas");
    canvas.width = leftCanvas.width + rightCanvas.width;
    canvas.height = Math.max(leftCanvas.height, rightCanvas.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(leftCanvas, 0, (canvas.height - leftCanvas.height) / 2);
    ctx.drawImage(
      rightCanvas,
      leftCanvas.width,
      (canvas.height - rightCanvas.height) / 2
    );

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 100);
    }, "image/png");
    return { annotationsDrawn };
  } catch (error) {
    console.error("Dual capture failed:", error);
    return null;
  }
};
